"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Una cifra que sube desde cero cuando entra en pantalla, una sola vez.
 *
 * ⚠️ **En el servidor se renderiza el número final, no un cero.** Si naciera en cero, un visitante sin
 * JavaScript —o Google— leería «0 funciones construidas», que es justo lo contrario de lo que dice el
 * bloque. La animación solo arranca después de montar, y el primer render del cliente es idéntico al
 * del servidor para que no haya desajuste de hidratación.
 *
 * Con `prefers-reduced-motion` no se anima: se queda el número, y ya está.
 */

/** Duración del conteo. Más de un segundo se siente lento; menos, no se ve. */
const MS_DURACION = 1100;

export function Contador({ valor, className }: { valor: number; className?: string }) {
  const [mostrado, setMostrado] = useState(valor);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || valor === 0) return;
    if (typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // Se pone en cero recién ahora: en el primer render tiene que valer lo mismo que en el servidor.
    setMostrado(0);

    let cuadro = 0;
    const observador = new IntersectionObserver(
      (entradas) => {
        if (!entradas[0].isIntersecting) return;
        observador.disconnect();

        const inicio = performance.now();
        const paso = (ahora: number) => {
          const t = Math.min(1, (ahora - inicio) / MS_DURACION);
          // Desaceleración cúbica: arranca rápido y se posa en el número, en vez de frenar en seco.
          const suave = 1 - (1 - t) ** 3;
          setMostrado(Math.round(valor * suave));
          if (t < 1) cuadro = requestAnimationFrame(paso);
        };
        cuadro = requestAnimationFrame(paso);
      },
      { threshold: 0.4 },
    );

    observador.observe(el);
    return () => {
      observador.disconnect();
      cancelAnimationFrame(cuadro);
    };
  }, [valor]);

  return (
    <span ref={ref} className={className}>
      {mostrado}
    </span>
  );
}
